import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  full_name: string;
  role: string;
  preferred_locale: string;
  organization_id: string | null;
}

interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  tenantSlug: string | null;
  setAuth: (user: User, token: string, refreshToken: string, tenantSlug: string) => void;
  setTenantSlug: (tenantSlug: string) => void;
  setToken: (token: string) => void;
  setTokens: (token: string, refreshToken: string) => void;
  updateUser: (partial: Partial<User>) => void;
  logout: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      token: null,
      refreshToken: null,
      tenantSlug: null,
      setAuth: (user, token, refreshToken, tenantSlug) =>
        set({ user, token, refreshToken, tenantSlug }),
      setTenantSlug: (tenantSlug) => set({ tenantSlug }),
      setToken: (token) => set({ token }),
      setTokens: (token, refreshToken) => set({ token, refreshToken }),
      updateUser: (partial) =>
        set((s) => ({ user: s.user ? { ...s.user, ...partial } : null })),
      logout: () => {
        set({ user: null, token: null, refreshToken: null, tenantSlug: null });
        // Nulling the store fields is not enough — Zustand's persist middleware
        // still keeps the serialised snapshot in localStorage under `qenq-auth`.
        // Clear it explicitly so a shared/kiosk browser doesn't rehydrate the
        // previous user on the next visit.
        try {
          localStorage.removeItem('qenq-auth');
        } catch {
          // Private-mode Safari can throw on localStorage — safe to ignore.
        }
        // Redirect to login to break any render loops
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      },
    }),
    {
      name: 'qenq-auth',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        tenantSlug: state.tenantSlug,
      }),
    },
  ),
);
